import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { IconButton } from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Guid } from 'guid-typescript';
import { toast } from 'react-toastify';
import {
  VisibilityOutlined as VisibilityOutlinedIcon,
  VisibilityOffOutlined as VisibilityOffOutlinedIcon
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';

import { RoutesConstants } from '@/main/routes/routesConstants';

// Services
import {
  getDefaultOptionDrop,
  getTranslateByKey,
  isNullUndefined,
  isNullUndefinedEmpty,
  isValueQueryParams,
  removeEmptyValues
} from '@/main/services/utils';
import { userCompanyType, userIsAdmin } from '@/main/services/utilsUser';
import { getOptionsStatus } from '@/main/services/data/data';
import { StatusIntegrationCompanyEnum } from '@/main/services/enum/StatusIntegrationCompanyEnum';
import { StatusRegisterEnum } from '@/main/services/enum/StatusRegisterEnum';

//Api
import { integrationIntegrationInterfaceCompanyApi } from '@/main/api';

//Models
import { KeyValue, KeyValueDefault } from '@/main/models/common';
import { IntegrationIntegrationInterfaceCompanySaveRequest } from '@/main/models/request';
import {
  IntegrationIntegrationInterfaceCompanyIPSResponse,
  IntegrationInterfaceResponse
} from '@/main/models/response';

//Reducer
import { getCountriesUser, useCountryReducer } from '@/main/store/country';
import {
  useIntegrationTypeReducer,
  resetReducerIntegrationInterface,
  getService,
  resetReducerService,
  getIntegrationInterface
} from '@/main/store/integrationType';
import {
  getIntegrationTrigger,
  useIntegrationTriggerReducer
} from '@/main/store/integrationTrigger';
import {
  getCompany,
  resetReducerCompany,
  useCompanyReducer
} from '@/main/store/company';
import {
  getCompanyType,
  resetReducerCompanyType,
  useCompanyTypeReducer
} from '@/main/store/companyType';
import {
  getPayloadFormat,
  useRequestFormatReducer
} from '@/main/store/RequestFormat';
import {
  getExcludeVariables,
  useExcludeVariablesReducer
} from '@/main/store/excludeVariables';
import {
  getDomainUsersByCompany,
  resetReducerDomainUsersByCompany,
  useDomainUsersByCompanyReducer
} from '@/main/store/domainUsersByCompany';

// Components
import KogAutoCompleteControlled from '@/main/components/shared/inputAutoComplete/autoCompleteControlled';
import ContainerScreen from '@/main/components/shared/containerScreen/containerLayout';
import KogInputTextControlledNew from '@/main/components/shared/inputText/inputTextControlledNew';
import KogInputTextLogin from '@/main/components/shared/inputText/inputTextLogin';
import Button from '@/main/components/shared/button/button';
import SecondaryButton from '@/main/components/shared/button/secondaryButton';
import Box from '@/main/components/shared/box';
import KogCheckBoxControlled from '@/main/components/shared/checkbox/checkBoxControlled';

//StyleGlobal
import { useStyles } from '../../login/styles';
import { getSecondaryColors } from '@/main/style/styleMaterialUIGlobal';

// Components/Styles Local
import IntegrationRegisterGrid from './integrationRegisterGrid/integrationRegisterGrid';
import ModalIntegration from './modalIntegration/modalIntegration';
import * as S from './styles';
import { IntegrationTypeEnum } from '@/main/services/enum/IntegrationTypeEnum';
import { IOption } from '@/main/components/shared/inputAutoCompleteCheck';
import KogInputAutoCompleteCheckControlled from '@/main/components/shared/inputAutoCompleteCheck/inputAutoCompleteCheckControlled';
import { IntegrationInterfaceEnum } from '@/main/services/enum/IntegrationInterfaceEnum';
import { RequestFormatApi } from '@/main/api/requestFormatApi';
import { IntegrationEnum } from '@/main/services/enum/IntegrationEnum';
import { CompanyTypeEnum } from '@/main/services/enum/CompanyTypeEnum';

export interface IGridList extends IntegrationIntegrationInterfaceCompanyIPSResponse {
  guidList: string;
}

export function IntegrationRegisterScreen() {
  const theme = useTheme();
  const styles = useStyles();

  const [searchParams, setSearchParams] = useSearchParams();
  const [isOpenModal, setIsOpenModal] = useState<boolean>(false);
  const [isLoadingSubmit, setIsLoadingSubmit] = useState<boolean>(false);
  const [inputPortVisible, setInputPortVisible] = useState<boolean>(false);
  const [inputTriggerVisible, setInputTriggerVisible] =
    useState<boolean>(false);
  const [showPayloadFormatField, setInputPayloadFormatVisible] =
    useState<boolean>(false);
  const [inputGetTokenUrlVisible, setInputGetTokenUrlVisible] =
    useState<boolean>(false);
  const [showExcludeVariablesDropdown, setShowExcludeVariablesDropdown] =
    useState<boolean>(false);

  const [inputCSFREndpointVisible, setInputCSRFEndpointVisible] =
    useState<boolean>(false);

  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [showCallbackPassword, setShowCallbackPassword] =
    useState<boolean>(false);
  const [isLoadingGet, setIsLoadingGet] = useState<boolean>(false);
  const [gridData, setGridData] = useState<IGridList[]>([]);
  const [selectGuidForEdit, setSelectGuidForEdit] = useState<string | null>(
    null
  );
  const [showIPRestrictionGrid, setShowIPRestrictionGrid] =
    useState<boolean>(false);
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [isOpenPayloadFormatModal, setIsOpenPayloadFormatModal] =
    useState(false);
  const [payloadFormats, setPayloadFormats] = useState(true);
  const [isLoadingPayloadFormats, setIsLoadingPayloadFormats] = useState(false);

  const guidIntegrationCompany = searchParams.get('guidIntegrationCompany');
  const handleClickShowPassword = () => setShowPassword(!showPassword);
  const handleClickShowCallbackPassword = () =>
    setShowCallbackPassword(!showCallbackPassword);
  const secondaryColors = getSecondaryColors(theme);
  const dispatch = useDispatch();

  // Reducers
  const navigate = useNavigate();
  const countryReducer = useCountryReducer();
  const companyTypeReducer = useCompanyTypeReducer();
  const companyReducer = useCompanyReducer();
  const requestFormatReducer = useRequestFormatReducer();
  const integration = useIntegrationTypeReducer();
  const integrationTriggerReducer = useIntegrationTriggerReducer();
  const excludeVariablesReducer = useExcludeVariablesReducer();
  const domainUsersByCompanyReducer = useDomainUsersByCompanyReducer();

  // Methods & Variables for Form
  const typeCompanyUser = userIsAdmin() ? null : userCompanyType();

  const optionDefaultCountryDrop =
    countryReducer.countriesUser.data !== null &&
    countryReducer.countriesUser.data.length === 1
      ? countryReducer.countriesUser.data[0]
      : null;

  const optionDefaultServiceDrop =
    integration.service.data !== null && integration.service.data.length === 1
      ? integration.service.data[0]
      : null;

  const optionDefaultCompanyDrop = getDefaultOptionDrop(
    companyReducer.company.data
  );
  const optionDefaultCompanyTypeDrop = getDefaultOptionDrop(
    companyTypeReducer.companyType.data,
    typeCompanyUser
  );

  const optionsStatus = getOptionsStatus();

  const INITIAL_FORM_STATE = {
    country: optionDefaultCountryDrop,
    companyType: optionDefaultCompanyDrop,
    company: optionDefaultCompanyDrop,
    service: optionDefaultServiceDrop as KeyValueDefault | null,
    interface: null as IntegrationInterfaceResponse | null,
    status: null as KeyValue | null | undefined,
    endpoint: '',
    username: '',
    password: '',
    accessKey: '',
    externalId: '',
    userAllowed: null as KeyValue | null,
    trigger: [] as IOption[] | null | undefined,
    port: null as number | null | undefined,
    payloadFormat: null as KeyValue | null | undefined,
    integrationTokenUrl: undefined as string | undefined,
    integrationCsrfTokenUrl: undefined as string | undefined,
    excludeVariables: [] as IOption[] | null | undefined,
    useCallback: false,
    callbackEndpoint: '',
    callbackUserName: '',
    callbackPassword: '',
    callbackAccessKey: '',
    callbackExternalId: ''
  };
  type IIntegrationRegisterForm = typeof INITIAL_FORM_STATE;

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
    watch,
    clearErrors,
    getValues
  } = useForm<IIntegrationRegisterForm>({
    mode: 'onChange',
    defaultValues: INITIAL_FORM_STATE
  });

  const watcherCountry = watch('country');
  const watcherIntegrationService = watch('service');
  const watcherIntegrationInterface = watch('interface');
  const watcherAccessKey = watch('accessKey');
  const watcherCompanyType = watch('companyType');
  const watcherCompany = watch('company');
  const watcherUseCallback = watch('useCallback');

  const isXnuupStandardApi =
    watcherIntegrationService?.default ===
    String(IntegrationEnum.XnuupStandardApis);

  const isReceiveUpdateQuoteInterface =
    watcherIntegrationInterface?.idIntegrationInterface ===
    IntegrationInterfaceEnum.ReceiveUpdateQuote;

  const isReceiveQuoteInterface =
    watcherIntegrationInterface?.idIntegrationInterface ===
    IntegrationInterfaceEnum.ReceiveQuote;

  const showCallbackSection =
    isReceiveQuoteInterface || isReceiveUpdateQuoteInterface;

  const hasAccessKey = watcherAccessKey && watcherAccessKey.length > 0;

  const hasCompanySelected =
    !isNullUndefined(watcherCompany) && !isNullUndefined(watcherCompany?.key);

  const isInsurer = watcherCompanyType?.key === String(CompanyTypeEnum.Insurer);

  const showUserAllowedDropdown =
    isXnuupStandardApi &&
    isReceiveUpdateQuoteInterface &&
    hasAccessKey &&
    isInsurer &&
    hasCompanySelected;

  const isDisabledCompanyTypeDrop = () =>
    !!guidIntegrationCompany ||
    watcherCountry === null ||
    companyTypeReducer.companyType.data === null ||
    optionDefaultCompanyTypeDrop !== null;

  const isDisabledCompanyDrop = () =>
    !!guidIntegrationCompany ||
    companyReducer.company.data === null ||
    getValues().companyType === null ||
    optionDefaultCompanyDrop !== null;

  const disabledIntegrationInterface =
    isNullUndefined(watcherIntegrationService) || isEdit;

  function onSubmit(data: IIntegrationRegisterForm) {
    setIsLoadingSubmit(true);

    let triggers: string[] = [];

    data.trigger &&
      data.trigger.map(item => {
        triggers.push(String(item.key));
      });

    let excludeVariables: string[] = [];

    data.excludeVariables &&
      data.excludeVariables.map(item => {
        excludeVariables.push(String(item.key));
      });

    const request: IntegrationIntegrationInterfaceCompanySaveRequest =
      removeEmptyValues({
        guidIntegration_IntegrationInterface_Company:
          guidIntegrationCompany || Guid.EMPTY,

        guidCountry: String(data.country?.key) || '',
        idCompanyType: String(data.companyType?.key) || '',
        guidCompany: String(data.company?.key) || '',
        guidIntegration: String(data.service?.key) || '',
        guidIntegrationInterface: String(data.interface?.key) || '',
        active:
          Number(data.status?.key) === StatusIntegrationCompanyEnum.Active
            ? true
            : false,
        integrationUrl: data.endpoint,
        integrationUser: data.username,
        integrationPassword: data.password,
        integrationAccessKey: data.accessKey,
        integrationExternalId: data.externalId,
        guidUserAllowedAccessKey: data.userAllowed
          ? String(data.userAllowed.key)
          : undefined,
        triggers,
        port: Number(data.port) || undefined,
        iPs: gridData,
        idIntegrationPayloadFormat: String(data.payloadFormat?.key),
        integrationTokenUrl: String(data?.integrationTokenUrl) || undefined,
        integrationCsrfTokenUrl:
          String(data?.integrationCsrfTokenUrl) || undefined,
        excludeVariables:
          excludeVariables.length > 0 ? excludeVariables : undefined,
        idUser: data?.userAllowed ? String(data.userAllowed.key) : undefined,
        useCallback: data.useCallback,
        callbackEndpoint: data.callbackEndpoint || undefined,
        callbackUserName: data.callbackUserName || undefined,
        callbackPassword: data.callbackPassword || undefined,
        callbackAccessKey: data.callbackAccessKey || undefined,
        callbackExternalId: data.callbackExternalId || undefined
      });
    integrationIntegrationInterfaceCompanyApi
      .save(request)
      .then(res => {
        const resData = res.data.result;
        toast.success(getTranslateByKey('information.savedSuccess'));
        navigate(RoutesConstants.integrationSearchIntegration);
      })
      .finally(() => {
        setIsLoadingSubmit(false);
      });
  }

  async function callCompanyApiDomain(
    countrySelected: KeyValue | null = null,
    valueSelected: KeyValue | null
  ) {
    setValue('company', null);

    const countryKey = isNullUndefined(countrySelected)
      ? String(watcherCountry?.key)
      : String(countrySelected?.key);
    if (valueSelected === null) return;

    dispatch(
      getCompany({
        guidCountry: countryKey,
        idCompanyType: String(valueSelected?.key),
        idStatusRegister: StatusRegisterEnum.Active
      })
    );
  }
  useEffect(() => {
    dispatch(getPayloadFormat({}));
  }, []);
  async function callRequestFormatApiDomain(
    countrySelected: KeyValue | null = null,
    valueSelected: KeyValue | null
  ) {
    dispatch(getPayloadFormat({}));
  }
  async function callIntegrationInterface(service: KeyValue | null) {
    if (!isValueQueryParams(service)) {
      setValue('interface', null);
      dispatch(resetReducerIntegrationInterface());
    }
    if (service === null) return;

    dispatch(
      getIntegrationInterface({
        idIntegrationType: 0,
        guidIntegration: String(service.key)
      })
    );
  }

  function displayFTPPortField(
    optionSelected: IntegrationInterfaceResponse | null
  ) {
    if (optionSelected) {
      if (optionSelected.isFtp) {
        return setInputPortVisible(true);
      }
    }
    setInputPortVisible(false);
  }

  function displayTriggerField(
    optionSelected: IntegrationInterfaceResponse | null
  ) {
    if (optionSelected) {
      if (
        optionSelected.idIntegrationInterface ===
          IntegrationInterfaceEnum.SendUpdates ||
        optionSelected.idIntegrationInterface ===
          IntegrationInterfaceEnum.SendFlekkOrderUpdate
      ) {
        if (!isLoadingGet) {
          setValue('trigger', null);
          clearErrors('trigger');
        } else {
          setIsLoadingGet(false);
        }
        if (
          !integrationTriggerReducer.integrationTriggers.isLoading &&
          isNullUndefined(integrationTriggerReducer.integrationTriggers.data)
        )
          dispatch(getIntegrationTrigger());
        return setInputTriggerVisible(true);
      } else {
        setInputTriggerVisible(false);
        setValue('trigger', null);
        clearErrors('trigger');
      }
    } else {
      setInputTriggerVisible(false);
      setValue('trigger', null);
      clearErrors('trigger');
    }
  }
  function displayPayloadFormatField(
    optionSelected: IntegrationInterfaceResponse | null
  ) {
    if (optionSelected) {
      if (
        optionSelected.idIntegrationInterface ===
        IntegrationInterfaceEnum.SendUpdates
      ) {
        if (!isLoadingGet) {
          setValue('payloadFormat', null);
          clearErrors('payloadFormat');
        } else {
          setIsLoadingGet(false);
        }
        dispatch(getPayloadFormat({}));
        return setInputPayloadFormatVisible(true);
      } else {
        setInputPayloadFormatVisible(false);
        setValue('payloadFormat', null);
        clearErrors('payloadFormat');
      }
    } else {
      setInputPayloadFormatVisible(false);
      setValue('payloadFormat', null);
      clearErrors('payloadFormat');
    }
  }

  function displayGetTokenAndCSRFUrl(
    optionsSelected: IntegrationInterfaceResponse | null
  ) {
    if (!optionsSelected) return;

    const getTokenOnly = [
      IntegrationInterfaceEnum.GenerateUrlBDEO,
      IntegrationInterfaceEnum.UpdateValuationFromBDEO,
      IntegrationInterfaceEnum.SendQuotesToSupplier,
      IntegrationInterfaceEnum.SendUpdates
    ];

    const getTokenAndCSRF = [
      IntegrationInterfaceEnum.SendClaimUpdates,
      IntegrationInterfaceEnum.SendQuoteToXnuup,
      IntegrationInterfaceEnum.UpdateVoucherStatusSISE,
      IntegrationInterfaceEnum.SearchDamagePayment,
      IntegrationInterfaceEnum.SendSavePortal
    ];

    const id = optionsSelected.idIntegrationInterface;

    setInputGetTokenUrlVisible(
      getTokenOnly.includes(id) || getTokenAndCSRF.includes(id)
    );

    setInputCSRFEndpointVisible(getTokenAndCSRF.includes(id));
  }

  function displayDropdownExcludeVariables(
    optionsSelected: IntegrationInterfaceResponse | null
  ) {
    if (
      optionsSelected &&
      (optionsSelected.idIntegrationInterface ==
        IntegrationInterfaceEnum.SendClaimUpdates ||
        optionsSelected.idIntegrationInterface ==
          IntegrationInterfaceEnum.GetClaimDetails ||
        optionsSelected.idIntegrationInterface ==
          IntegrationInterfaceEnum.SendUpdates)
    ) {
      setShowExcludeVariablesDropdown(true);
      dispatch(
        getExcludeVariables(String(optionsSelected.idIntegrationInterface))
      );
    } else {
      setShowExcludeVariablesDropdown(false);
      setValue('excludeVariables', null);
    }
  }

  function handleButtonDeletar(guid: string) {
    setGridData(prev => {
      const updatedGridData = prev.filter(item => item.guidList !== guid);
      return updatedGridData;
    });
  }

  function handleButtonEdit(guid: string) {
    setSelectGuidForEdit(guid);
    setIsOpenModal(true);
  }

  function loadInfoIntegrationinterfaceCompanyData(guid: string) {
    setIsLoadingGet(true);

    integrationIntegrationInterfaceCompanyApi
      .getIntegrationinterfaceCompanyData(guid)
      .then(res => {
        const resData = res.data.result;

        setValue('country', {
          key: resData.guidCountry,
          value: resData.country,
          default: ''
        });

        setValue('companyType', {
          key: String(resData.idCompanyType),
          value: resData.companyType
        });

        callCompanyApiDomain(
          {
            key: resData.guidCountry,
            value: resData.country
          },
          {
            key: resData.idCompanyType,
            value: resData.companyType
          }
        );

        setValue('company', {
          key: resData.guidCompany,
          value: resData.companyName
        });

        if (resData.guidCompany) {
          dispatch(getDomainUsersByCompany(resData.guidCompany));
        }

        setValue('service', {
          key: resData.guidIntegration,
          value: resData.integrationName,
          default: String(resData.idIntegration)
        });

        if (resData.guidIntegration && resData.integrationInterface) {
          callIntegrationInterface({
            key: resData.guidIntegration,
            value: resData.integrationInterface
          });
        }

        if (resData.isFtp) {
          displayFTPPortField({
            key: resData.guidIntegrationInterface,
            value: resData.integrationInterface,
            isFtp: resData.isFtp,
            idIntegrationInterface: resData.idIntegrationInterface,
            idIntegrationType: resData.idIntegrationType,
            integrationTypeName: resData.integrationTypeName
          });
        }

        setValue('interface', {
          key: resData.guidIntegrationInterface,
          value: resData.integrationInterface,
          isFtp: resData.isFtp,
          idIntegrationInterface: resData.idIntegrationInterface,
          idIntegrationType: resData.idIntegrationType,
          integrationTypeName: resData.integrationTypeName
        });

        displayTriggerField({
          key: resData.guidIntegrationInterface,
          value: resData.integrationInterface,
          isFtp: resData.isFtp,
          idIntegrationInterface: resData.idIntegrationInterface,
          idIntegrationType: resData.idIntegrationType,
          integrationTypeName: resData.integrationTypeName
        });
        displayPayloadFormatField({
          key: resData.guidIntegrationInterface,
          value: resData.integrationInterface,
          isFtp: resData.isFtp,
          idIntegrationInterface: resData.idIntegrationInterface,
          idIntegrationType: resData.idIntegrationType,
          integrationTypeName: resData.integrationTypeName
        });

        setValue('status', {
          key: resData.active
            ? StatusIntegrationCompanyEnum.Active
            : StatusIntegrationCompanyEnum.Suspended,
          value: resData.active
            ? getTranslateByKey('statusUser.active')
            : getTranslateByKey('statusUser.suspended')
        });

        if (resData.integrationUrl) {
          setValue('endpoint', resData.integrationUrl);
        }
        if (resData.integrationUser) {
          setValue('username', resData.integrationUser);
        }
        if (resData.integrationPassword) {
          setValue('password', resData.integrationPassword);
        }
        if (resData.integrationAccessKey) {
          setValue('accessKey', resData.integrationAccessKey);
        }
        if (resData.integrationExternalID) {
          setValue('externalId', resData.integrationExternalID);
        }
        if (resData.port) {
          setInputPortVisible(true);
          setValue('port', resData.port);
        }
        if (resData.triggers) {
          setInputTriggerVisible(true);
          setValue('trigger', resData.triggers);
        }
        const ipsData: IGridList[] = [];
        if (resData.ipRestriction && resData.ipRestriction.length > 0) {
          resData.ipRestriction.map(item => {
            ipsData.push({
              ...item,
              guidList: item.guidIntegr_IntegrInterface_Company_IPAddress
            });
          });
          setGridData(ipsData);
        }
        setValue('payloadFormat', {
          key: resData.idIntegrationPayloadFormat,
          value: resData.payloadName
        });

        setValue('integrationTokenUrl', resData.integrationTokenUrl);
        setValue('integrationCsrfTokenUrl', resData.integrationCsrfTokenUrl);
        displayGetTokenAndCSRFUrl({
          key: resData.guidIntegrationInterface,
          value: resData.integrationInterface,
          isFtp: resData.isFtp,
          idIntegrationInterface: resData.idIntegrationInterface,
          idIntegrationType: resData.idIntegrationType,
          integrationTypeName: resData.integrationTypeName
        });
        displayDropdownExcludeVariables({
          key: resData.guidIntegrationInterface,
          value: resData.integrationInterface,
          isFtp: resData.isFtp,
          idIntegrationInterface: resData.idIntegrationInterface,
          idIntegrationType: resData.idIntegrationType,
          integrationTypeName: resData.integrationTypeName
        });

        if (resData.excludeVariables && resData.excludeVariables.length > 0) {
          const excludeVariablesOptions: IOption[] =
            resData.excludeVariables.map(varName => ({
              key: varName.value,
              value: varName.value
            }));
          setValue('excludeVariables', excludeVariablesOptions);
        }

        if (resData.idUser && resData.userName) {
          setValue('userAllowed', {
            key: resData.idUser,
            value: resData.userName
          });
        }

        setValue('useCallback', !!resData.useCallback);
        if (resData.callbackEndpoint) {
          setValue('callbackEndpoint', resData.callbackEndpoint);
        }
        if (resData.callbackUserName) {
          setValue('callbackUserName', resData.callbackUserName);
        }
        if (resData.callbackPassword) {
          setValue('callbackPassword', resData.callbackPassword);
        }
        if (resData.callbackAccessKey) {
          setValue('callbackAccessKey', resData.callbackAccessKey);
        }
        if (resData.callbackExternalId) {
          setValue('callbackExternalId', resData.callbackExternalId);
        }
      })
      .finally(() => {});
  }

  function handleBack() {
    navigate(RoutesConstants.integrationSearchIntegration);
  }

  useEffect(() => {
    if (!showUserAllowedDropdown) {
      setValue('userAllowed', null);
      clearErrors('userAllowed');
    }
  }, [showUserAllowedDropdown, setValue, clearErrors]);

  useEffect(() => {
    if (!showCallbackSection) {
      setValue('useCallback', false);
      setValue('callbackEndpoint', '');
      setValue('callbackUserName', '');
      setValue('callbackPassword', '');
      setValue('callbackAccessKey', '');
      setValue('callbackExternalId', '');
    }
  }, [showCallbackSection, setValue]);

  useEffect(() => {
    if (!inputTriggerVisible) {
      setValue('trigger', null);
    }

    if (!inputPortVisible) {
      setValue('port', null);
    }
  }, [inputTriggerVisible, inputPortVisible]);

  useEffect(() => {
    dispatch(getCountriesUser());
    // Get services withot filter integration type
    dispatch(getService(0));
    dispatch(getCompanyType());

    if (isNullUndefined(optionDefaultCompanyTypeDrop))
      setValue('companyType', null);

    if (
      isNullUndefined(watcherIntegrationService) ||
      isNullUndefined(integration.integrationInterface.data)
    )
      setValue('interface', null);

    return () => {
      dispatch(resetReducerCompany());
      dispatch(resetReducerService());
      dispatch(resetReducerIntegrationInterface());
      dispatch(resetReducerDomainUsersByCompany());
    };
  }, []);

  useEffect(() => {
    if (isNullUndefined(watcherCountry)) return;
    callCompanyApiDomain(watcherCountry, optionDefaultCompanyTypeDrop);
  }, [watcherCountry?.key, optionDefaultCompanyTypeDrop?.key]);

  useEffect(() => {
    if (!isOpenModal) {
      setSelectGuidForEdit(null);
    }
  }, [isOpenModal]);

  useEffect(() => {
    if (watcherIntegrationInterface) {
      if (
        watcherIntegrationInterface.idIntegrationType ===
        IntegrationTypeEnum.Provided
      ) {
        setShowIPRestrictionGrid(true);
      } else {
        setShowIPRestrictionGrid(false);
        setGridData([]);
      }
    } else {
      setShowIPRestrictionGrid(false);
    }
  }, [watcherIntegrationInterface]);
  useEffect(() => {
    setPayloadFormats(true);
  }, []);
  useEffect(() => {
    if (guidIntegrationCompany) {
      loadInfoIntegrationinterfaceCompanyData(guidIntegrationCompany);
      setIsEdit(true);
    }
  }, [guidIntegrationCompany]);

  // CONSTANTS LAYOUT
  const titleScreen = `${getTranslateByKey(
    'module.integration'
  )} > ${getTranslateByKey('screen.registerIntegration')}`;

  function validateField(value: IOption[]) {
    if (value.length === 0) {
      setValue('trigger', null);
      return false;
    }
    setValue('trigger', value);
    return true;
  }

  return (
    <>
      <ContainerScreen hasBackground title={titleScreen}>
        <Box title={getTranslateByKey('information.filters')}>
          <form onSubmit={handleSubmit(onSubmit)} noValidate>
            <S.BoxFilters>
              <S.RowInput>
                <KogAutoCompleteControlled
                  required
                  label={getTranslateByKey('information.country')}
                  control={control}
                  options={countryReducer.countriesUser.data}
                  isLoading={countryReducer.countriesUser.isLoading}
                  nameCustom="country"
                  rulesCustom={{ required: true }}
                  errorMessageCustom={
                    errors.country?.type === 'required'
                      ? getTranslateByKey('information.countryRequired', true)
                      : ''
                  }
                  hasErrorCustom={!!errors.country}
                  onChangeCustom={(optionSelected: KeyValue | null) => {
                    // callCompanyTypeApiDomain(optionSelected);
                    callCompanyApiDomain(optionSelected, null);
                  }}
                  autoHighlightCustom={!!optionDefaultCountryDrop}
                  setValueCustom={setValue}
                  disabled={
                    !!guidIntegrationCompany ||
                    optionDefaultCountryDrop !== null
                  }
                  id="id-registerIntegration-select-country"
                />

                <KogAutoCompleteControlled
                  nameCustom="companyType"
                  control={control}
                  label={getTranslateByKey('information.companyType')}
                  rulesCustom={{ required: true }}
                  hasErrorCustom={!!errors.companyType}
                  errorMessageCustom={
                    errors.companyType?.type === 'required'
                      ? getTranslateByKey(
                          'information.companyTypeRequired',
                          true
                        )
                      : ''
                  }
                  options={companyTypeReducer.companyType.data}
                  isLoading={companyTypeReducer.companyType.isLoading}
                  onChangeCustom={(optionSelected: KeyValue | null) => {
                    callCompanyApiDomain(getValues().country, optionSelected);

                    setValue('userAllowed', null);
                    clearErrors('userAllowed');
                  }}
                  disabled={isDisabledCompanyTypeDrop()}
                  valueDefaultKey={optionDefaultCompanyTypeDrop?.key}
                  setValueCustom={setValue}
                  isDefaultValueUnique
                  id="id-registerIntegration-select-companyType"
                />

                <KogAutoCompleteControlled
                  label={getTranslateByKey('information.company')}
                  isDefaultValueUnique
                  control={control}
                  nameCustom="company"
                  rulesCustom={{ required: true }}
                  options={companyReducer.company.data}
                  isLoading={companyReducer.company.isLoading}
                  onChangeCustom={(optionSelected: KeyValue | null) => {
                    setValue('userAllowed', null);
                    clearErrors('userAllowed');

                    if (optionSelected) {
                      dispatch(
                        getDomainUsersByCompany(String(optionSelected.key))
                      );
                    } else {
                      dispatch(resetReducerDomainUsersByCompany());
                    }
                  }}
                  disabled={isDisabledCompanyDrop()}
                  valueDefaultKey={optionDefaultCompanyDrop?.key}
                  setValueCustom={setValue}
                  id="id-registerIntegration-select-company"
                />
              </S.RowInput>

              <S.RowInput>
                <KogAutoCompleteControlled
                  required
                  label={getTranslateByKey('information.service')}
                  control={control}
                  options={integration.service.data}
                  isLoading={integration.service.isLoading}
                  nameCustom="service"
                  rulesCustom={{ required: true }}
                  hasErrorCustom={!!errors.service}
                  onChangeCustom={(
                    optionSelected: (KeyValue & Partial<KeyValueDefault>) | null
                  ) => {
                    callIntegrationInterface(optionSelected);
                    displayPayloadFormatField(null);
                    displayFTPPortField(null);
                    displayTriggerField(null);
                    displayGetTokenAndCSRFUrl(null);
                    displayDropdownExcludeVariables(null);
                    setValue('userAllowed', null);
                    clearErrors('userAllowed');
                  }}
                  autoHighlightCustom={!!optionDefaultServiceDrop}
                  setValueCustom={setValue}
                  disabled={optionDefaultServiceDrop !== null || isEdit}
                  id="id-registerIntegration-select-service"
                />

                <KogAutoCompleteControlled
                  label={getTranslateByKey('information.interface')}
                  control={control}
                  nameCustom="interface"
                  rulesCustom={{ required: true }}
                  hasErrorCustom={!!errors.interface}
                  options={integration.integrationInterface.data}
                  isLoading={integration.integrationInterface.isLoading}
                  onChangeCustom={optionSelected => {
                    displayFTPPortField(
                      optionSelected as IntegrationInterfaceResponse | null
                    );
                    displayTriggerField(
                      optionSelected as IntegrationInterfaceResponse | null
                    );
                    displayPayloadFormatField(
                      optionSelected as IntegrationInterfaceResponse | null
                    );
                    displayGetTokenAndCSRFUrl(
                      optionSelected as IntegrationInterfaceResponse | null
                    );
                    displayDropdownExcludeVariables(
                      optionSelected as IntegrationInterfaceResponse | null
                    );
                    setValue('userAllowed', null);
                    clearErrors('userAllowed');
                  }}
                  isDefaultValueUnique
                  autoOnChangeCustom
                  disabled={disabledIntegrationInterface}
                  setValueCustom={setValue}
                  id="id-registerIntegration-select-interface"
                />

                <KogAutoCompleteControlled
                  nameCustom="status"
                  label={getTranslateByKey('information.status')}
                  control={control}
                  options={optionsStatus}
                  isLoading={false}
                  valueDefaultKey={StatusIntegrationCompanyEnum.Active}
                  setValueCustom={setValue}
                  hasErrorCustom={!!errors.status}
                  rulesCustom={{ required: true }}
                  errorMessageCustom={
                    errors.status?.type === 'required'
                      ? getTranslateByKey('information.statusRequired', true)
                      : ''
                  }
                  onChangeCustom={() => {}}
                  id="id-registerIntegration-select-status"
                />
              </S.RowInput>

              <S.RowInput>
                <KogInputTextControlledNew
                  name="endpoint"
                  label={getTranslateByKey('information.endpoint')}
                  control={control}
                  id="id-registerIntegration-txt-endpoint"
                  inputProps={{ maxLength: 250 }}
                />

                <KogInputTextControlledNew
                  name="username"
                  label={getTranslateByKey('information.userName')}
                  control={control}
                  id="id-registerIntegration-txt-username"
                />

                <S.InputLogin>
                  <Controller
                    control={control}
                    name="password"
                    render={({
                      field: { ref, ...field },
                      fieldState: { error }
                    }) => (
                      <KogInputTextLogin
                        inputRef={ref}
                        {...field}
                        id="id-registerIntegration-txt-password"
                        variant="outlined"
                        label={getTranslateByKey('information.password')}
                        positionIcon="end"
                        type={showPassword ? 'text' : 'password'}
                        className={styles.inputTextCustom}
                        icon={
                          <IconButton
                            aria-label="toggle password visibility"
                            onClick={handleClickShowPassword}
                            edge="end">
                            {showPassword ? (
                              <VisibilityOutlinedIcon
                                style={{ color: secondaryColors.contrastText }}
                              />
                            ) : (
                              <VisibilityOffOutlinedIcon
                                style={{ color: secondaryColors.contrastText }}
                              />
                            )}
                          </IconButton>
                        }
                        title={getTranslateByKey('information.password')}
                      />
                    )}
                  />
                </S.InputLogin>
              </S.RowInput>

              <S.RowInput>
                {inputGetTokenUrlVisible && (
                  <KogInputTextControlledNew
                    name="integrationTokenUrl"
                    control={control}
                    label={getTranslateByKey('information.getTokenEndpoint')}
                    id="id-registerIntegration-txt-getTokenEndpoint"
                  />
                )}

                {inputCSFREndpointVisible && (
                  <KogInputTextControlledNew
                    name="integrationCsrfTokenUrl"
                    control={control}
                    label={getTranslateByKey(
                      'information.getCSRFTokenEndpoint'
                    )}
                    id="id-registerIntegration-txt-getCSRFTokenEndpoint"
                  />
                )}
                <KogInputTextControlledNew
                  name="accessKey"
                  label={getTranslateByKey('information.accessKey')}
                  control={control}
                  id="id-registerIntegration-txt-accessKey"
                  inputProps={{ maxLength: 500 }}
                />
                {showUserAllowedDropdown && (
                  <KogAutoCompleteControlled
                    nameCustom="userAllowed"
                    control={control}
                    label={getTranslateByKey(
                      'information.userAllowedToUseAccessKey'
                    )}
                    rulesCustom={{ required: true }}
                    hasErrorCustom={!!errors.userAllowed}
                    options={
                      domainUsersByCompanyReducer.domainUsersByCompany.data
                    }
                    isLoading={
                      domainUsersByCompanyReducer.domainUsersByCompany.isLoading
                    }
                    onChangeCustom={(optionSelected: KeyValue | null) => {}}
                    disabled={
                      domainUsersByCompanyReducer.domainUsersByCompany.data
                        ?.length === 0
                    }
                    setValueCustom={setValue}
                    id="id-registerIntegration-select-userAllowed"
                  />
                )}

                <KogInputTextControlledNew
                  name="externalId"
                  label={getTranslateByKey('information.externalID')}
                  control={control}
                  id="id-registerIntegration-txt-externalId"
                  inputProps={{ maxLength: 500 }}
                />
                {showPayloadFormatField && (
                  <KogAutoCompleteControlled
                    nameCustom="payloadFormat"
                    control={control}
                    label="Payload Format"
                    rulesCustom={{ required: true }}
                    hasErrorCustom={!!errors.payloadFormat}
                    errorMessageCustom={
                      errors.payloadFormat?.type === 'required'
                        ? getTranslateByKey(
                            'information.companyTypeRequired',
                            true
                          )
                        : ''
                    }
                    options={requestFormatReducer.data}
                    isLoading={requestFormatReducer.isLoading}
                    onChangeCustom={(optionSelected: KeyValue | null) => {
                      callRequestFormatApiDomain(null, optionSelected);
                    }}
                    disabled={false}
                    setValueCustom={setValue}
                    isDefaultValueUnique
                    id="id-registerIntegration-select-payloadFormat"
                  />
                )}
                {inputTriggerVisible && (
                  <KogInputAutoCompleteCheckControlled
                    name="trigger"
                    control={control}
                    options={
                      integrationTriggerReducer.integrationTriggers.data || []
                    }
                    label={getTranslateByKey('information.trigger')}
                    isLoading={
                      integrationTriggerReducer.integrationTriggers.isLoading
                    }
                    controllerProps={{ rules: { required: true } }}
                    customInputProps={{
                      required: true,
                      id: 'id-registerIntegration-multiSelect-trigger'
                    }}
                    errorMessageCustom={
                      errors.trigger?.type === 'required'
                        ? getTranslateByKey('information.triggerRequired', true)
                        : ''
                    }
                    selectAllLabel={getTranslateByKey('information.selectAll')}
                    setValueCustom={setValue}
                  />
                )}

                {inputPortVisible && (
                  <KogInputTextControlledNew
                    name="port"
                    required
                    errorMessageCustom={
                      errors.port?.type === 'required'
                        ? getTranslateByKey('information.portRequired', true)
                        : ''
                    }
                    label={getTranslateByKey('information.port')}
                    control={control}
                    hasOnlyNumberInteger
                    id="id-registerIntegration-txt-port"
                  />
                )}

                {showExcludeVariablesDropdown && (
                  <KogInputAutoCompleteCheckControlled
                    name="excludeVariables"
                    control={control}
                    options={
                      excludeVariablesReducer.excludeVariables.data || []
                    }
                    label={getTranslateByKey('information.excludeVariables')}
                    isLoading={
                      excludeVariablesReducer.excludeVariables.isLoading
                    }
                    controllerProps={{ rules: { required: false } }}
                    customInputProps={{
                      required: false,
                      id: 'id-registerIntegration-multiSelect-excludeVariables'
                    }}
                    selectAllLabel={getTranslateByKey('information.selectAll')}
                    setValueCustom={setValue}
                  />
                )}
                {showCallbackSection && (
                  <KogCheckBoxControlled
                    nameCustom="useCallback"
                    control={control}
                    label="Callback"
                    removeMargin
                  />
                )}
              </S.RowInput>
            </S.BoxFilters>

            {showCallbackSection && watcherUseCallback && (
              <S.BoxFilters>
                <S.RowInput>
                  <KogInputTextControlledNew
                    name="callbackEndpoint"
                    required
                    label={getTranslateByKey('information.endpoint')}
                    control={control}
                    id="id-registerIntegration-txt-callbackEndpoint"
                    inputProps={{ maxLength: 250 }}
                  />

                  <KogInputTextControlledNew
                    name="callbackUserName"
                    label={getTranslateByKey('information.userName')}
                    control={control}
                    id="id-registerIntegration-txt-callbackUsername"
                  />

                  <S.InputLogin>
                    <Controller
                      control={control}
                      name="callbackPassword"
                      render={({ field: { ref, ...field } }) => (
                        <KogInputTextLogin
                          inputRef={ref}
                          {...field}
                          id="id-registerIntegration-txt-callbackPassword"
                          variant="outlined"
                          label={getTranslateByKey('information.password')}
                          positionIcon="end"
                          type={showCallbackPassword ? 'text' : 'password'}
                          className={styles.inputTextCustom}
                          icon={
                            <IconButton
                              aria-label="toggle callback password visibility"
                              onClick={handleClickShowCallbackPassword}
                              edge="end">
                              {showCallbackPassword ? (
                                <VisibilityOutlinedIcon
                                  style={{
                                    color: secondaryColors.contrastText
                                  }}
                                />
                              ) : (
                                <VisibilityOffOutlinedIcon
                                  style={{
                                    color: secondaryColors.contrastText
                                  }}
                                />
                              )}
                            </IconButton>
                          }
                          title={getTranslateByKey('information.password')}
                        />
                      )}
                    />
                  </S.InputLogin>
                </S.RowInput>

                <S.RowInput>
                  <KogInputTextControlledNew
                    name="callbackAccessKey"
                    label={getTranslateByKey('information.accessKey')}
                    control={control}
                    id="id-registerIntegration-txt-callbackAccessKey"
                    inputProps={{ maxLength: 500 }}
                  />

                  <KogInputTextControlledNew
                    name="callbackExternalId"
                    label={getTranslateByKey('information.externalID')}
                    control={control}
                    id="id-registerIntegration-txt-callbackExternalId"
                    inputProps={{ maxLength: 500 }}
                  />
                </S.RowInput>
              </S.BoxFilters>
            )}

            <S.AreaFooter $hasGridData={!!gridData && gridData.length > 0}>
              {showIPRestrictionGrid && (
                <S.AreaIpRestriction>
                  <S.BoxTitleGrid>
                    <S.TitleGrid>
                      {getTranslateByKey('information.ipRestriction')}
                    </S.TitleGrid>
                    <SecondaryButton
                      onClick={() => setIsOpenModal(prev => !prev)}>
                      {getTranslateByKey('information.addRestriction')}
                    </SecondaryButton>
                  </S.BoxTitleGrid>

                  <IntegrationRegisterGrid
                    gridData={gridData}
                    buttonDelete={handleButtonDeletar}
                    buttonEdit={handleButtonEdit}
                  />
                </S.AreaIpRestriction>
              )}

              <S.AreaButton>
                <SecondaryButton
                  type="button"
                  id="id-registerIntegration-btn-back"
                  onClick={handleBack}>
                  {getTranslateByKey('information.back')}
                </SecondaryButton>
                <Button
                  type="submit"
                  id="id-registerIntegration-btn-save"
                  isLoading={isLoadingSubmit}>
                  {getTranslateByKey('information.save')}
                </Button>
              </S.AreaButton>
            </S.AreaFooter>
          </form>
        </Box>
      </ContainerScreen>

      <ModalIntegration
        isOpenModal={isOpenModal}
        setIsOpenModal={setIsOpenModal}
        gridData={gridData}
        setGridData={setGridData}
        selectGuidForEdit={selectGuidForEdit}
      />
    </>
  );
}

export default IntegrationRegisterScreen;
